import { useCallback, useMemo } from 'react';

import { RbacPermission } from '~/types/Rbac';

import { useAllPermissionsQuery } from './useAllPermissionsQuery';

type Props = {
  permissions?: RbacPermission[];
};

export function usePermissions({ permissions = [] }: Props) {
  const { data, error, isError, isFetching, isUninitialized, refetch } =
    useAllPermissionsQuery();

  // `checkPermission` is a public API taking plain strings (callers pass
  // literals), so read through a string-indexable view of the same map.
  const results: Record<string, Record<string, boolean>> | undefined = data;

  // Deriving the array from its own serialization keeps the identity stable
  // for callers that build it inline.
  const permissionsKey = JSON.stringify(permissions);
  const stablePermissions = useMemo(
    (): RbacPermission[] => JSON.parse(permissionsKey),
    [permissionsKey],
  );

  const checkPermission = useCallback(
    (resourceId: string, resourceType: string) =>
      results?.[resourceId]?.[resourceType] ?? false,
    [results],
  );

  const hasSomePermissions = useMemo(() => {
    if (!stablePermissions.length) return true;
    return stablePermissions.some(({ resourceId, resourceType }) =>
      checkPermission(resourceId, resourceType),
    );
  }, [stablePermissions, checkPermission]);

  // `PermissionsGate` redirects on "not loading and no access", so an
  // unresolved request has to read as loading — never as denied. A caller that
  // asked for nothing is gating on nothing, and never waits.
  const isLoading =
    stablePermissions.length > 0 && (isUninitialized || isFetching);

  // A failed request leaves every pair reading `false`, which is
  // indistinguishable from a real denial. Callers that act on "no access" —
  // `PermissionsGate` above all — need the failure itself, and a way to ask
  // again, so they can tell "you may not" from "we could not find out".
  return {
    error,
    hasSomePermissions,
    isError,
    isLoading,
    checkPermission,
    refetch,
  };
}
